import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, User, Mail, Shield, Image as ImageIcon, BadgeCheck } from "lucide-react";
import api from "@/lib/api";

export default function Account() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [plan, setPlan] = useState({ projects: null, unlocked: null });

  useEffect(() => {
    if (!user || user === false) return;
    let cancelled = false;
    const fetchPlan = () => api.get("/visualiser/projects")
      .then((r) => {
        if (cancelled) return;
        const total = (r.data || []).length;
        const unlocked = (r.data || []).filter((p) => p.unlocked).length;
        setPlan({ projects: total, unlocked });
      })
      .catch(() => { /* anonymous or transient — ignore */ });
    fetchPlan();
    // Refresh on window focus so a customer returning from Stripe / Workspace
    // sees fresh counts without a manual reload.
    const onFocus = () => fetchPlan();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; window.removeEventListener("focus", onFocus); };
  }, [user]);

  if (!user || user === false) return null;

  // Prefer "loading" copy until the first fetch has resolved so we never
  // briefly tell a customer who just created their first project that they
  // have "no projects yet".
  const planValue = plan.unlocked == null
    ? "Loading your projects…"
    : plan.unlocked > 0
      ? `${plan.unlocked} unlocked project${plan.unlocked === 1 ? "" : "s"} · ${plan.projects} total`
      : plan.projects > 0
        ? `Free preview · ${plan.projects} project${plan.projects === 1 ? "" : "s"} saved`
        : "Free preview · ready to start your first design";

  return (
    <div className="max-w-2xl mx-auto px-5 py-10 lg:py-14" data-testid="account-page">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-brand-ink mb-8">Account</h1>

        <div className="bg-white rounded-3xl p-6 shadow-soft space-y-5 mb-5">
          <Row icon={<User size={16} />} label="Name" value={user.name} />
          <Row icon={<Mail size={16} />} label="Email" value={user.email} />
          <Row
            icon={<BadgeCheck size={16} />}
            label="Your plan"
            value={planValue}
            testId="account-plan-value"
          />
          {user.role === "admin" && (
            <Row icon={<Shield size={16} />} label="Role" value={<span className="bg-brand-teal/10 text-brand-teal px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-widest">Admin</span>} />
          )}
        </div>

        <Link to="/projects" data-testid="account-projects-link"
          className="block bg-white rounded-3xl p-5 shadow-soft hover:shadow-floating transition-all mb-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ImageIcon size={16} className="text-brand-teal" />
              <div>
                <div className="text-sm font-semibold text-brand-ink">My projects</div>
                <div className="text-xs text-brand-muted mt-0.5">All your saved designs, photos and schemes</div>
              </div>
            </div>
            <span className="text-brand-muted">›</span>
          </div>
        </Link>

        {user.role === "admin" && (
          <Link to="/admin" data-testid="account-admin-link"
            className="block bg-white rounded-3xl p-5 shadow-soft hover:shadow-floating transition-all mb-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield size={16} className="text-brand-muted" />
                <div>
                  <div className="text-sm font-semibold text-brand-ink">Admin dashboard</div>
                  <div className="text-xs text-brand-muted mt-0.5">Visualiser metrics, projects and users</div>
                </div>
              </div>
              <span className="text-brand-muted">›</span>
            </div>
          </Link>
        )}

        <button onClick={() => { logout(); nav("/"); }} data-testid="account-logout-btn"
          className="w-full bg-white rounded-3xl p-5 shadow-soft hover:shadow-floating transition-all flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogOut size={16} className="text-brand-muted" />
            <span className="text-sm font-semibold text-brand-ink">Sign out</span>
          </div>
          <span className="text-brand-muted">›</span>
        </button>
      </motion.div>
    </div>
  );
}

function Row({ icon, label, value, testId }) {
  return (
    <div className="flex items-center gap-4">
      <span className="w-9 h-9 rounded-xl bg-brand-sand flex items-center justify-center text-brand-muted">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-brand-muted uppercase tracking-widest font-semibold">{label}</div>
        <div className="text-sm text-brand-ink truncate" data-testid={testId}>{value}</div>
      </div>
    </div>
  );
}
